import {
	createSlice,
	createEntityAdapter,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import {
	getPreviousItClaim,
	getRequestLinesByFormId,
	postRequestLines as post,
} from "api";
import { selectAllAttachment } from "../attachment/attachmentsSlice";
import {
	selectRequestFormData,
	selectRequestFormId,
	selectRequestFormSupplementaryDataActiveCpe,
	selectRequestFormTypeId,
} from "../requestForm/requestFormSlice";
import { selectUserOrganisation } from "reducers/securitySlice";
import { CME_CONFIG } from "../constants";
import { selectCpeFormData } from "../cpeForm/cpeFormSlice";

export const fetchRequestLinesByFormId = createAsyncThunk(
	"requestLines/fetchRequestLinesById",
	//need the thunkApi's getState to retrieve auth token
	async (formId, { rejectWithValue, dispatch }) => {
		try {
			const data = await getRequestLinesByFormId({ formId });
			dispatch(requestLinesReceived(data));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const validateAllRequestLines = createAsyncThunk(
	"requestLines/validateAllRequestLines",
	async (_, { dispatch, getState }) => {
		const state = getState();
		const organisation = selectUserOrganisation(state);
		const requestData = selectRequestFormData(state);
		const cpeData = selectCpeFormData(state);
		const formTypeId =
			requestData.formTypeId === 0
				? cpeData.formTypeId
				: requestData.formTypeId;
		const requestLines = selectAllRequestLines(state);
		const attachments = selectAllAttachment(state);
		const attachmentCount = attachments.length;
		const isClaim = [2, 3, 8, 9, 10].includes(formTypeId);
		const errors =
			requestData.formTypeId === 5
				? 0 //we don't have req lines in SMO leave form
				: requestLines.reduce((acc, d) => {
						const errors = validateRequestLine(
							d,
							attachmentCount,
							isClaim,
							organisation
						);
						dispatch(requestLineUpserted({ ...d, errors }));
						return acc + errors.length;
				  }, 0);
		dispatch(validationErrorsEdited(errors));
		return errors;
	}
);

export const postRequestLines = createAsyncThunk(
	"requestLines/postRequestLines",
	async (data, { dispatch, rejectWithValue }) => {
		try {
			const response = await post(data);
			dispatch(requestLinesReceived(response));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

const requestLinesAdapter = createEntityAdapter({
	selectId: (requestLine) => requestLine.requestId,
});

export const CATEGORY_LOOKUP = [
	{
		id: 17,
		code: 17,
		domain: "Course Expense Category",
		description: "Course/Conference/Study fees only",
	},
	{
		id: 19,
		code: 19,
		domain: "Course Expense Category",
		description: "Accommodation",
	},
	{
		id: 20,
		code: 20,
		domain: "Course Expense Category",
		description: "Flights (visas, taxes)",
	},
	{
		id: 21,
		code: 21,
		domain: "Course Expense Category",
		description: "Meals",
	},
	{
		id: 22,
		code: 22,
		domain: "Course Expense Category",
		description: "Other",
		excludeOrgs: ["Northland", "WDHB"],
	},
	{
		id: 13,
		code: 13,
		domain: "General Expense Category",
		description: "Fees",
		excludeOrgs: ["Northland", "WDHB"],
	},
	{
		id: 12,
		code: 12,
		domain: "General Expense Category",
		description: "Books / Journals / Periodicals",
	},
	{
		id: 32,
		code: 32,
		domain: "General Expense Category",
		description: "Subscriptions",
	},
	{
		id: 33,
		code: 33,
		domain: "General Expense Category",
		description: "Other (please provide details)",
		excludeOrgs: ["Northland"],
	},
	{
		id: 36,
		code: 36,
		domain: "IT Expense Category",
		description: "Phone",
	},
	{
		id: 259,
		code: 259,
		domain: "IT Expense Category",
		description: "Tablet / iPad",
	},
	{
		id: 14,
		code: 14,
		domain: "IT Expense Category",
		description: "Computer/Laptop",
	},
	{
		id: 37,
		code: 37,
		domain: "IT Expense Category",
		description: "Printer",
		excludeOrgs: ["Northland"],
	},
	{
		id: 15,
		code: 15,
		domain: "IT Expense Category",
		description: "Software",
	},
	{
		id: 35,
		code: 35,
		domain: "IT Expense Category",
		description: "Other (please provide details)",
		excludeOrgs: ["Northland", "WDHB"],
	},
	{
		id: 277,
		code: 277,
		domain: "IT Expense Category",
		description: "IT Accessories",
	},
	{
		id: 278,
		code: 278,
		domain: "General Expense Category",
		description: "IT accessories (<$1,000)",
	},
	{
		id: 279,
		code: 279,
		domain: "Course Expense Category",
		description: "Mileage",
	},
	{
		id: 280,
		code: 280,
		domain: "Course Expense Category",
		description: "Koha",
		excludeOrgs: ["Counties Manukau", "Northland"],
	},
	{
		id: 281,
		code: 281,
		domain: "General Expense Category",
		description: `Clinical equipment (<$1,000)`,
		excludeOrgs: ["Counties Manukau"],
	},
	{
		id: 282,
		code: 282,
		domain: "IT Expense Category",
		description: "Clinical equipment",
		excludeOrgs: ["Counties Manukau"],
	},
	{
		id: 283,
		code: 283,
		domain: "WRE Category",
		description: "APC / Professional Membership",
	},
	{
		id: 284,
		code: 284,
		domain: "WRE Category",
		description: "Insurance",
	},
	{
		id: 285,
		code: 285,
		domain: "General Expense Category",
		description: "Supervision",
	},
	{
		id: 286,
		code: 286,
		domain: "Course Expense Category",
		description: "Transport (taxi, tolls, parking, rental car)",
	},
	{
		id: 287,
		code: 287,
		domain: "Course Expense Category",
		description: "Parking",
		excludeOrgs: ["Northland", "WDHB"],
	},
];

export const DOLLAR_PER_KM = 1.04;

export const isItCategory = (categoryLookupId) => {
	return !categoryLookupId
		? false
		: categoryLookupId === 278 || //IT accessory
				CATEGORY_LOOKUP.find((l) => l.code === categoryLookupId)
					?.domain === "IT Expense Category";
};

const validateRequestLine = (data, attachmentCount, isClaim, organisation) => {
	let errors = [];
	const {
		categoryLookupId,
		invoiceNumber,
		grossAmount,
		gstAmount,
		netAmount,
		invoiceDate,
		description,
	} = data;
	const config = CME_CONFIG[organisation];
	if (!categoryLookupId || categoryLookupId === 0)
		errors.push("Category is required");
	if (!invoiceNumber && isClaim & (categoryLookupId !== 279))
		errors.push("Invoice Number is required");
	if (categoryLookupId === 279 && !description)
		errors.push("Please provide details of your mileage claim (from/to)");
	//IT accessories on other cme claim form
	//OR any line in IT request form require a description
	if (isItCategory(categoryLookupId) && !description)
		errors.push("IT equipments and accessories require a description");
	if (!invoiceDate && isClaim) errors.push("Date incurred is required");
	if (!grossAmount || grossAmount <= 0)
		errors.push("Gross Amount cannot be zero or less");
	if (gstAmount > grossAmount)
		errors.push("GST amount cannot be greater than Gross amount");
	if ((!attachmentCount || attachmentCount === 0) && isClaim)
		errors.push("At least one attachment is required");
	if (
		categoryLookupId === 278 &&
		grossAmount > config.ItClaimLimitWithoutPreapproval
	)
		errors.push(
			`IT accessories above ${config.ItClaimLimitWithoutPreapproval} in gross amount require pre-approval`
		);
	if (isItCategory(categoryLookupId) && netAmount > config.ItClaimLineLimit) {
		let nzd = new Intl.NumberFormat("en-NZ", {
			style: "currency",
			currency: "NZD",
			maximumFractionDigits: 0,
		});
		errors.push(
			`IT request/claim items cannot exceed ${nzd.format(
				config.ItClaimLineLimit
			)} excluding GST`
		);
	}
	if (
		categoryLookupId === 281 &&
		grossAmount > config.ItClaimLimitWithoutPreapproval
	)
		errors.push(
			`Clinical equipment cannot exceed ${config.ItClaimLimitWithoutPreapproval} in gross amount for this type of claim`
		);
	return errors;
};

export const REQUEST_LINE_TEMPLATE = {
	requestId: 0,
	requestDate: null,
	parentId: 0,
	formId: 0,
	statusId: 0,
	categoryLookupId: null,
	description: null,
	numberOfUnits: null,
	unitType: null,
	amount: null,
	netAmount: null,
	gstAmount: null,
	emolumentAmount: null,
	grossAmount: null,
	smartAmount: null,
	cmeDeductionAmount: null,
	invoiceDate: null,
	invoiceNumber: null,
	isDeleted: false,
	isGst: true,
	prorateFactor: null,
	fbtRate: null,
	adjustmentAmount: null,
	currency: "NZD",
};

export const getNewCpeRequestLines = (formId) => [
	{
		...REQUEST_LINE_TEMPLATE,
		formId,
		categoryLookupId: 19,
		unitType: "NIGHTS",
	},
	{ ...REQUEST_LINE_TEMPLATE, formId, categoryLookupId: 17 },
	{ ...REQUEST_LINE_TEMPLATE, formId, categoryLookupId: 20 },
	{ ...REQUEST_LINE_TEMPLATE, formId, categoryLookupId: 21 },
	{ ...REQUEST_LINE_TEMPLATE, formId, categoryLookupId: 22 },
];

const isPrevClaimSearchable = (formTypeId, categoryLookupId) => {
	return formTypeId === 4 && [36, 259, 14].includes(categoryLookupId);
};

const checkAndFetchPrevClaim = async (oldState, action, form) => {
	const request = {
		...oldState,
		[action.questionId]: action.value,
	};

	if (!isPrevClaimSearchable(form.formTypeId, request.categoryLookupId)) {
		//remove prevline
		const { prevLine, ...rest } = request;
		const newRequest = { prevLine: null, ...rest };
		return newRequest;
	}

	//only fetch previous IT claim on change of category lookup id
	const prevLine =
		action.questionId === "categoryLookupId"
			? await getPreviousItClaim({
					categoryLookupId: request.categoryLookupId,
					personId: form.personId,
					excludeFormId: form.formId,
					invoiceDate: form.startDate,
			  })
			: request.prevLine;

	return { ...request, isPrevClaimSearched: true, prevLine };
};

function calculateGstAmount(isFx, questionId, request, value) {
	if (isFx) return 0;
	if (questionId === "gstAmount") return Number(value); //manual GST update, just receive the value
	if (request.isGst) {
		//if this line has GST
		if (questionId === "grossAmount" || questionId === "isGst")
			//and gross amount updated
			return Number(
				((Number(request.grossAmount) / 115) * 15).toFixed(2)
			);
		else return request.gstAmount; //otherwise just return existing gst
	}
	return 0; //non gst
}

function calculateGrossAmount(isFx, questionId, request, value, mileageRate) {
	if (isFx) {
		return Number(request.grossAmount);
	}

	return questionId === "numberOfUnits" && request.categoryLookupId === 279
		? Number(value) * (mileageRate ?? DOLLAR_PER_KM) //mileage km update
		: questionId === "mileageRateId"
		? request.numberOfUnits * (mileageRate ?? DOLLAR_PER_KM) //mileage rate update
		: questionId === "grossAmount" //manual gross amount update
		? Number(value)
		: request.grossAmount; //all other cases
}

export const requestLineEdited = createAsyncThunk(
	"requestLines/requestLineEdited",
	//need the thunkApi's getState to retrieve auth token
	async (action, { getState, dispatch }) => {
		const { questionId, id, value } = action;

		//get the other values on the request line
		const { cme } = getState();
		const { requestLines, requestForm, parentForm, admin } = cme;
		const { mileageRates } = admin;

		const request = await checkAndFetchPrevClaim(
			requestLines.entities[id],
			action,
			requestForm.data
		);

		//if form is IT request, or the parent form is IT request,
		//or if it is IT Accessories type, it attracts emolument
		const isIt =
			(requestForm.data.formTypeId === 4 ||
				parentForm.data.formTypeId === 4 ||
				request.categoryLookupId === 278) &&
			request.categoryLookupId !== 282; //exclude clinical equipment from emolument

		//Foreign currency flag - if this is true, we need to unset GST and related fields later
		const isFx = request.currency && request.currency !== "NZD";

		const gstAmount = calculateGstAmount(isFx, questionId, request, value);

		//for mileage, we need to recalc gross amount
		const mileageRate = mileageRates.find(
			(mr) => mr.id === request.mileageRateId
		);
		const grossAmount = calculateGrossAmount(
			isFx,
			questionId,
			request,
			value,
			mileageRate?.rate
		);

		const fxRate =
			isFx && grossAmount
				? (request.foreignAmount / grossAmount).toFixed(4)
				: null;

		const foreignAmount = isFx ? request.foreignAmount : null;

		//recalc net amount. gross/gst is always manually updated
		const netAmount = Number(grossAmount) - Number(gstAmount);

		//recalc emolument, based on gross amount
		const emolumentAmount = isIt
			? calculateEmolument(Number(grossAmount))
			: 0;

		//create new request data to upsert
		const newReq = {
			...request,
			gstAmount,
			netAmount,
			emolumentAmount,
			grossAmount,
			fxRate,
			foreignAmount,
			mileageRate: mileageRate?.rate,
		};
		dispatch(requestLineUpserted(newReq));
	}
);

const initialState = requestLinesAdapter.getInitialState({
	status: "idle",
	error: null,
	validationErrors: 0,
});

const calculateEmolument = (amount = 0) => {
	return Number(((amount / (1 - 0.39)) * 0.39).toFixed(4));
};

const requestLineSlice = createSlice({
	name: "requestLines",
	initialState,
	reducers: {
		requestLinesReceived(state, action) {
			//calculated field isGst
			//if gst is null (new line) or has value, isGst is true so that it auto calcs gst
			const data = action.payload.map((r) => {
				return {
					...r,
					isGst:
						r.gstAmount === null || r.gstAmount > 0 ? true : false,
				};
			});
			requestLinesAdapter.addMany(state, data);
		},
		requestLineDeleted(state, action) {
			requestLinesAdapter.removeOne(state, action.payload);
		},
		requestLineUpserted(state, action) {
			requestLinesAdapter.upsertOne(state, action.payload);
		},
		requestLineAdded(state, action) {
			const { questionId, value } = action.payload;
			const req = { requestId: 0, [questionId]: value }; //zero signifies new record
			requestLinesAdapter.addOne(state, req);
		},
		validationErrorsEdited(state, action) {
			state.validationErrors = action.payload;
		},
	},
	extraReducers: {
		RESET_CME: (state, action) => {
			return initialState;
		},
		[postRequestLines.pending]: (state, action) => {
			state.status = "loading";
		},
		[postRequestLines.fulfilled]: (state, action) => {
			state.status = "idle";
		},
		[postRequestLines.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchRequestLinesByFormId.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchRequestLinesByFormId.fulfilled]: (state, action) => {
			state.status = "idle";
		},
		[fetchRequestLinesByFormId.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
	},
});

export const {
	requestLineUpserted,
	requestLinesReceived,
	requestLineAdded,
	requestLineDeleted,
	validationErrorsEdited,
} = requestLineSlice.actions;

export const {
	selectAll: selectAllRequestLines,
	selectIds: selectRequestLineIds,
	selectById: selectRequestLineById,
} = requestLinesAdapter.getSelectors((state) => state?.cme?.requestLines);

export const selectRequestLineStatus = (state) => state.cme.requestLines.status;

export const selectRequestClaimAmount = createSelector(
	[selectAllRequestLines],
	(r) => {
		return r.reduce((acc, val) => {
			return val.emolumentAmount !== 0
				? acc + val.grossAmount + val.emolumentAmount
				: acc + val.netAmount;
		}, 0);
	}
);

export const selectMaxRequestClaimGrossAmount = createSelector(
	[selectAllRequestLines],
	(r) => {
		return r
			.map((i) => i.grossAmount)
			.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))[0];
	}
);

export const selectRequestClaimGrossAmount = createSelector(
	[selectAllRequestLines],
	(r) => {
		return r.reduce((acc, val) => {
			return val.emolumentAmount !== 0
				? acc + val.grossAmount
				: acc + val.netAmount;
		}, 0);
	}
);

export const selectRequestClaimEmolumentAmount = createSelector(
	[selectAllRequestLines],
	(r) => {
		return r.reduce((acc, val) => {
			return acc + val.emolumentAmount;
		}, 0);
	}
);

export const selectDefaultRequestLines = createSelector(
	[
		selectRequestFormSupplementaryDataActiveCpe,
		selectRequestFormId,
		selectRequestFormTypeId,
	],
	(activeCpe, formId, formTypeId) => {
		return getDefaultRequestLines(formId, formTypeId, activeCpe);
	}
);

export const getDefaultRequestLines = (formId, formTypeId, activeCpe) => {
	return [
		{
			...REQUEST_LINE_TEMPLATE,
			formId,
			prorateFactor: formTypeId === 8 ? activeCpe?.proratedFte : null,
		},
	];
};

export default requestLineSlice.reducer;
